import { yupResolver } from '@hookform/resolvers/yup';
import * as Ramda from 'ramda';
import * as Yup from 'yup';
import React from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  Grid,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

import CbButton from '../../../../../../../../../components/Buttons/CbButton';
import { Flexbox } from '../../../../../../../../../components/layout/Flexbox';
import { Show } from '../../../../../../../../../components/Show';
import { CardContainer } from './shared/StyledCardContainer';
import { ControlledTextField } from './shared/common';

export const SpecifiedEntityExclusionModal = ({
  data: modalProps,
  close,
}: SpecifiedEntityExclusionModalProps) => {
  const { showDescription } = modalProps;
  const scrollableRef = React.useRef<null | HTMLDivElement>(null);
  const formMethods = useForm<SpecifiedEntityFormValues>({
    defaultValues: {
      exclusions: modalProps.exclusions,
    },
    resolver: yupResolver(schema(showDescription)),
  });

  const fieldArrayMethods = useFieldArray({
    control: formMethods.control,
    name: 'exclusions',
  });

  const handleAddExclusion = ({ scroll }: { scroll: boolean }) => {
    fieldArrayMethods.append({
      name: '',
      description: '',
    });

    if (scroll) {
      setTimeout(
        () => scrollableRef.current?.scrollIntoView({ behavior: 'smooth' }),
        300
      );
    }
  };

  React.useEffect(() => {
    /* When component first mounts, check if at least one entity exists. If so, dont add a card by default */
    if (formMethods.getValues().exclusions[0]?.name) {
      return;
    }

    handleAddExclusion({ scroll: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <FormProvider {...formMethods}>
      <form
        onSubmit={formMethods.handleSubmit(
          Ramda.compose(close, modalProps.onSubmit)
        )}
      >
        <DialogContent style={{ padding: '1rem 2.5rem' }}>
          <Show when={!fieldArrayMethods.fields.length}>
            <Box p={4} textAlign="center">
              <Typography variant="body1" color="primary">
                No Specified Entity Exclusions
              </Typography>
            </Box>
          </Show>

          {fieldArrayMethods.fields.map((field, index) => {
            return (
              <CardContainer key={field.id} elevation={3}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <ControlledTextField
                      label="Entity Name"
                      required
                      name={`exclusions.${index}.name`}
                    />
                  </Grid>

                  {showDescription && (
                    <Grid item xs={12}>
                      <ControlledTextField
                        minRows={4}
                        multiline
                        required
                        label="Entity Description"
                        name={`exclusions.${index}.description`}
                      />
                    </Grid>
                  )}
                </Grid>
                <Button
                  className="contrast-text"
                  style={{ position: 'absolute', top: 5, right: 5 }}
                  onClick={() => fieldArrayMethods.remove(index)}
                >
                  Remove
                </Button>
              </CardContainer>
            );
          })}
          <div ref={scrollableRef} />
        </DialogContent>
        <DialogActions>
          <Flexbox>
            <Button
              startIcon={<AddIcon style={{ pointerEvents: 'none' }} />}
              className="contrast-text"
              onClick={() => handleAddExclusion({ scroll: true })}
            >
              Add Entity
            </Button>
            <Box>
              <CbButton onClick={close} styleName="cancel">
                Cancel
              </CbButton>
              <CbButton type="submit" styleName="ctaButton">
                Save
              </CbButton>
            </Box>
          </Flexbox>
        </DialogActions>
      </form>
    </FormProvider>
  );
};

export const SpecifiedEntityExclusionModalConfig = {
  SpecifiedEntityExclusionModal: {
    component: SpecifiedEntityExclusionModal,
    config: {
      title: 'Specified Entity Exclusion Endorsement',
      fullWidth: true,
      maxWidth: 'md',
    },
  },
};

//types
type SpecifiedEntityExclusionModalProps = {
  data: {
    showDescription: boolean;
    onSubmit: (formValues: SpecifiedEntityFormValues) => void;
    exclusions: SpecifiedEntityExclusions[];
  };
  close: () => void;
};

export type SpecifiedEntityFormValues = {
  exclusions: SpecifiedEntityExclusions[];
};

export type SpecifiedEntityExclusions = {
  name: string;
  description: string;
};

//validations
const schema = (showDescription: boolean) =>
  Yup.object().shape({
    exclusions: Yup.array().of(
      Yup.object().shape({
        name: Yup.string().trim().label('Entity name').required(),
        ...(showDescription
          ? {
              description: Yup.string()
                .trim()
                .label('Entity description')
                .required(),
            }
          : {}),
      })
    ),
  });
